<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    AIDisclosureParagraphEditType,
    AIDisclosureParagraphType,
} from '@/services/ai-disclosure/type/ai-disclosure.enum';
import { disclosureOptimizationTypeMap } from '@/presentation/pages/ai-disclosure/components/constants';
import { useEventListener } from '@vueuse/core';

const { t } = useI18n();

const props = withDefaults(defineProps<{
    isFloating?: boolean;
    isSelection?: boolean;
    paragraphType: AIDisclosureParagraphType | undefined;
}>(), {
    isFloating: false,
    isSelection: true,
});

const baseTypeList = [
    AIDisclosureParagraphEditType.POLISH,
    AIDisclosureParagraphEditType.SIMPLIFY,
    AIDisclosureParagraphEditType.EXPAND
];

const targetTypeList = computed(() => {
    if (props.isSelection) {
        return baseTypeList;
    }

    switch (props.paragraphType) {
    case AIDisclosureParagraphType.BACKGROUND:
        return [
            ...baseTypeList, AIDisclosureParagraphEditType.ADD_BACKGROUND
        ];
    case AIDisclosureParagraphType.REFERENCE:
        return [
            ...baseTypeList, AIDisclosureParagraphEditType.ADD_REFERENCE
        ];
    case AIDisclosureParagraphType.DRAWBACKS:
        return [
            ...baseTypeList, AIDisclosureParagraphEditType.ADD_DRAWBACKS
        ];
    case AIDisclosureParagraphType.TECH_PROBLEM:
        return [
            ...baseTypeList, AIDisclosureParagraphEditType.ADD_PROBLEM
        ];
    case AIDisclosureParagraphType.TECH_EFFECT:
        return [
            ...baseTypeList, AIDisclosureParagraphEditType.ADD_EFFECT
        ];
    case AIDisclosureParagraphType.EMBODIMENT:
        return [
            ...baseTypeList, AIDisclosureParagraphEditType.ADD_EMBODIMENT
        ];
    case AIDisclosureParagraphType.TECH_MEANS:
        return baseTypeList;
    default:
        return [];
    }
});

const disclosureEditTypeListRef = ref(null);

const compConfigMap = computed(() => {
    return disclosureOptimizationTypeMap.filter((item) => targetTypeList.value.includes(item.disclosureParagraphEditType));
});

const emits = defineEmits<{
    (event: 'toggleFloatingStatus', type: AIDisclosureParagraphEditType): void;
    (event: 'hiddenFloating'): void;
}>();

function toggleFloatingStatus(editType: AIDisclosureParagraphEditType) {
    emits('toggleFloatingStatus', editType);
}

function handleDocumentClick(event) {
    if (disclosureEditTypeListRef.value && !disclosureEditTypeListRef.value.contains(event.target) && !window.getSelection().toString()) {
        emits('hiddenFloating');
    }
}

useEventListener(document, 'mousedown', handleDocumentClick);
</script>

<template>
    <div ref="disclosureEditTypeListRef" class="disclosure-edit-type-list lab-border-rounded">
        <div
            class="floating-disclosure-content__select-menu w-[240px] p-2"
        >
            <div
                v-for="item in compConfigMap"
                :key="item.disclosureParagraphEditType"
                class="flex cursor-pointer items-center gap-1 p-1 hover:bg-grey-20"
                @click="toggleFloatingStatus(item.disclosureParagraphEditType)"
            >
                <component :is="item.icon" class="h-6 w-6 !fill-gray-450" />

                <span class="text-sm text-black">{{ t(item.labelKey) }}</span>
            </div>
        </div>
    </div>
</template>
